import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Map as LeafletMap, TileLayer, Polygon, Popup } from 'react-leaflet';
import proj4 from 'proj4';
import L from 'leaflet';
import VisibilitySensor from 'react-visibility-sensor';

import 'leaflet/dist/leaflet.css';

proj4.defs(
  'EPSG:2100',
  '+proj=tmerc +lat_0=0 +lon_0=24 +k=0.9996 +x_0=500000 +y_0=0 +ellps=GRS80 +towgs84=-199.87,74.79,246.62,0,0,0,0 +units=m +no_defs'
);

export function parseWktPolygon(wkt) {
  if (!wkt || typeof wkt !== 'string') {
    return [];
  }

  const text = wkt.trim();
  const isMulti = /^MULTIPOLYGON/i.test(text);

  let inner = text
    .replace(/^MULTIPOLYGON\s*/i, '')
    .replace(/^POLYGON\s*/i, '')
    .trim();

  if (inner.startsWith('(')) {
    inner = inner.slice(1);
  }
  if (inner.endsWith(')')) {
    inner = inner.slice(0, -1);
  }
  if (!isMulti && inner.startsWith('(')) {
    inner = inner.slice(1);
  }
  if (!isMulti && inner.endsWith(')')) {
    inner = inner.slice(0, -1);
  }

  const parts = isMulti ? inner.split(/\)\s*,\s*\(/) : [inner];

  return parts
    .map((part) => {
      const clean = part.replace(/^\(+|\)+$/g, '');
      const matches = [...clean.matchAll(/(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)/g)];
      return matches.map((m) => [parseFloat(m[1]), parseFloat(m[2])]);
    })
    .filter((ring) => ring.length >= 3);
}

function egsaRingToLatLngs(ring) {
  return ring.map(([x, y]) => {
    const [lng, lat] = proj4('EPSG:2100', 'EPSG:4326', [x, y]);
    return [lat, lng];
  });
}

const PROPERTY_LABELS = {
  aa: 'Α/Α',
  a_m: 'Α.Μ.',
  id: 'ID',
  id1: 'ID',
  company: 'Εταιρεία',
  power_mw: 'Ισχύς (MW)',
  max_power: 'Μέγ. ισχύς (MW)',
  thesh: 'Θέση',
  katastash_descr: 'Κατάσταση',
  katastasi: 'Κατάσταση',
  kal_perifereia: 'Περιφέρεια',
  kal_dhmos: 'Δήμος',
  kal_dhm_enothta: 'Δημοτική ενότητα',
  kal_perif_enothta: 'Περιφ. ενότητα',
  imerominia: 'Ημερομηνία',
  imerominia_ekdosis_adeias: 'Ημ. έκδοσης άδειας',
  epo: 'ΕΠΟ',
  aa_trop: 'Α/Α τροπ.',
  area_m2: 'Εμβαδόν (m²)',
  count_ag: 'Αριθμός Α/Γ',
  arithmos_typikon_ag: 'Αριθμός τυπικών Α/Γ',
  count_grby_diam_agg: 'Αριθμός αγωγών',
  paratiriseis: 'Παρατηρήσεις',
  parts: 'Τμήματα',
};

function formatPropertyLabel(key) {
  return PROPERTY_LABELS[key] || key;
}

function PropertiesPopup({ properties }) {
  if (!properties || typeof properties !== 'object') {
    return <span>Δεν υπάρχουν ιδιότητες.</span>;
  }

  const entries = Object.entries(properties).filter(
    ([, value]) => value !== null && value !== undefined && String(value).trim() !== ''
  );

  if (!entries.length) {
    return <span>Δεν υπάρχουν ιδιότητες.</span>;
  }

  const mid = Math.ceil(entries.length / 2);
  const leftColumn = entries.slice(0, mid);
  const rightColumn = entries.slice(mid);

  const renderColumn = (columnEntries) => (
    <table className="table table-sm table-bordered mb-0" style={{ fontSize: '0.85rem', width: '100%' }}>
      <tbody>
        {columnEntries.map(([key, value]) => (
          <tr key={key}>
            <th style={{ whiteSpace: 'nowrap' }}>{formatPropertyLabel(key)}</th>
            <td>{String(value)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
      <div style={{ flex: 1, minWidth: 0 }}>{renderColumn(leftColumn)}</div>
      <div style={{ flex: 1, minWidth: 0 }}>{renderColumn(rightColumn)}</div>
    </div>
  );
}

class WktGeometryMap extends Component {
  constructor(props) {
    super(props);
    this.mapRef = React.createRef();
    const polygons = parseWktPolygon(props.wkt).map(egsaRingToLatLngs);
    this.state = {
      polygons,
      center: props.center || [39.0742, 21.8243],
      zoom: props.zoom || 7,
    };
  }

  componentDidMount() {
    this.fitBounds();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.wkt !== this.props.wkt) {
      const polygons = parseWktPolygon(this.props.wkt).map(egsaRingToLatLngs);
      this.setState({ polygons }, () => this.fitBounds());
    }
  }

  renderPopup() {
    const { properties } = this.props;
    return (
      <Popup maxWidth={640} minWidth={480}>
        <PropertiesPopup properties={properties} />
      </Popup>
    );
  }

  fitBounds() {
    if (!this.mapRef.current || !this.state.polygons.length) {
      return;
    }
    const map = this.mapRef.current.leafletElement;
    const bounds = L.latLngBounds([]);
    this.state.polygons.forEach((ring) => {
      ring.forEach((latLng) => bounds.extend(latLng));
    });
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [24, 24] });
    }
  }

  handleVisibility = (visible) => {
    if (visible && this.mapRef.current) {
      this.mapRef.current.leafletElement.invalidateSize();
      this.fitBounds();
    }
  };

  render() {
    const { polygons, center, zoom } = this.state;
    const { height, properties } = this.props;
    const hasProperties = properties && typeof properties === 'object' && Object.keys(properties).length > 0;

    if (!polygons.length) {
      return null;
    }

    return (
      <VisibilitySensor onChange={this.handleVisibility}>
        <LeafletMap
          ref={this.mapRef}
          style={{ width: '100%', height: height || 360 }}
          center={center}
          zoom={zoom}
          scrollWheelZoom={false}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution="&copy; OpenStreetMap contributors"
          />
          {polygons.map((positions, index) => (
            <Polygon
              key={index}
              positions={positions}
              color="#3399ff"
              fillColor="#3399ff"
              fillOpacity={0.25}
              weight={2}
            >
              {hasProperties && this.renderPopup()}
            </Polygon>
          ))}
        </LeafletMap>
      </VisibilitySensor>
    );
  }
}

WktGeometryMap.propTypes = {
  wkt: PropTypes.string,
  properties: PropTypes.object,
  height: PropTypes.number,
  center: PropTypes.arrayOf(PropTypes.number),
  zoom: PropTypes.number,
};

export default WktGeometryMap;
